import {getCookie, setCookie} from '@vio/core/src/_web/utils/cookies'
import {cookieSecuritySettings} from '@vio/core/src/_web/utils/cookies/cookieSecuritySettings'
import {isBrowser} from '@vio/core/src/_web/utils/environment'
import {Page} from '@vio/core/src/analytics/types/Events'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {getDomainName} from '@vio/core/src/utils/url'

const BOOST_EXPEDIA_COOKIE_NAME = 'boostExpediaBackgroundTabTriggered'
const COOKIE_MAX_AGE = 1 // 1 day (24 hours)

/**
 * Check if the boost expedia background tab redirect has been triggered in the last 24 hours
 * @returns true if the cookie exists (redirect was already triggered)
 */
export const hasBackgroundTabBeenTriggered = (): boolean => {
  const cookie = getCookie(BOOST_EXPEDIA_COOKIE_NAME)
  return !!cookie
}

/**
 * Set the cookie to indicate that the boost expedia background tab redirect has been triggered
 * This cookie will expire after 24 hours
 */
export const setBackgroundTabTriggered = (): void => {
  const {secure, sameSite} = cookieSecuritySettings(true)
  const domain = !isBrowser ? '' : getDomainName(window.location.hostname)

  setCookie(BOOST_EXPEDIA_COOKIE_NAME, 'true', {
    expires: COOKIE_MAX_AGE,
    secure,
    sameSite,
    domain
  })
}

export const getTopOffersHasExpediaMemberDeal = (topOffers: SearchOffer[]) => {
  return topOffers.slice(1).some(offer => offer?.metadata?.signInDealAvailable)
}

export const getExpOfferIndexFromTopOffers = (
  offers: SearchOffer[]
): number => {
  return offers?.findIndex(offer => offer?.providerCode === 'EXP') ?? -1
}

interface ShouldTriggerExpediaBackgroundTabParams {
  clickedOffer: SearchOffer
  topOffers: SearchOffer[]
  isEligible: boolean
  pageName: Page
}

interface ShouldTriggerExpediaBackgroundTabResult {
  shouldTrigger: boolean
  expOffer: SearchOffer | undefined
}

/**
 * Determines if the Expedia background tab redirect should be triggered
 *
 * Added as part of 2a7f5ff0-expedia-back-tab
 *
 * Conditions:
 * 1. isEligible: brand capability (mobile) and meta traffic
 * 2. Clicked offer is not EXP, and not Vio outside the SRP
 * 3. Cookie hasn't been set (not triggered in last 24 hours)
 * 4. Top offers contain an EXP offer that is not the primary offer
 */
export const shouldTriggerExpediaBackgroundTabForOffer = ({
  clickedOffer,
  topOffers,
  isEligible,
  pageName
}: ShouldTriggerExpediaBackgroundTabParams): ShouldTriggerExpediaBackgroundTabResult => {
  // Check if it's eligible for the feature
  if (!isEligible) {
    return {shouldTrigger: false, expOffer: undefined}
  }

  // Vio offer clicks only trigger on the SRP
  if (
    pageName !== Page.Search &&
    (clickedOffer?.providerCode === 'FHT' ||
      clickedOffer?.intermediaryProvider === 'FHT')
  ) {
    return {shouldTrigger: false, expOffer: undefined}
  }

  // Check if clicked offer is EXP
  if (
    clickedOffer?.providerCode === 'EXP' ||
    clickedOffer?.intermediaryProvider === 'EXP'
  ) {
    return {shouldTrigger: false, expOffer: undefined}
  }

  // Check if cookie has been set (already triggered in last 24 hours)
  if (hasBackgroundTabBeenTriggered()) {
    return {shouldTrigger: false, expOffer: undefined}
  }

  // Check top offers for EXP offer
  const expOfferIndex = getExpOfferIndexFromTopOffers(topOffers)
  const expOffer = expOfferIndex > 0 ? topOffers[expOfferIndex] : undefined

  if (!expOffer) {
    return {shouldTrigger: false, expOffer: undefined}
  }

  // All conditions met
  return {shouldTrigger: true, expOffer}
}

interface ShouldTriggerExpediaBackgroundTabForHotelCardParams {
  topOffers: SearchOffer[]
  isEligible: boolean
}

/**
 * Determines if the Expedia background tab redirect should be triggered for mobile hotel card clicks
 *
 * Added as part of 2a7f5ff0-expedia-back-tab
 *
 * Conditions:
 * 1. isEligible: brand capability (mobile), SRP, meta traffic and no OTA redirect
 * 2. Cookie hasn't been set (not triggered in last 24 hours), shared with offer clicks
 * 3. Top offers contain an EXP offer that is not the primary offer
 */
export const shouldTriggerExpediaBackgroundTabForHotelCard = ({
  topOffers,
  isEligible
}: ShouldTriggerExpediaBackgroundTabForHotelCardParams): ShouldTriggerExpediaBackgroundTabResult => {
  // Check if it's eligible for the feature
  if (!isEligible) {
    return {shouldTrigger: false, expOffer: undefined}
  }

  // Check if cookie has been set (already triggered in last 24 hours)
  if (hasBackgroundTabBeenTriggered()) {
    return {shouldTrigger: false, expOffer: undefined}
  }

  // Check top offers for EXP offer
  const expOfferIndex = getExpOfferIndexFromTopOffers(topOffers)
  const expOffer = expOfferIndex > 0 ? topOffers[expOfferIndex] : undefined

  if (!expOffer) {
    return {shouldTrigger: false, expOffer: undefined}
  }

  // All conditions met
  return {shouldTrigger: true, expOffer}
}
